import {
  AlertColor,
  Box,
  Button,
  Card,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Toolbar,
  debounce,
} from '@mui/material';
import * as yup from 'yup';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TitleText from '../../components/formlib/TitleText';
import SearchBox from '../../components/formlib/SearchBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactComponent as Filtericon } from '../../assets/images/FilterIconsvg.svg';
import { FormattedMessage } from 'react-intl';
import { right } from '@popperjs/core';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import {
  DateString,
  OptionType,
  Order,
  progessNotesStatusFilter,
} from '../../utils/type';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import { Formik, FormikProps } from 'formik';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import dayjs, { Dayjs } from 'dayjs';
import MediumTypography from '../../components/formlib/MediumTypography';
import ProgressNotesTableRow from './ProgressNotesTableRow';
import AppPagination from '../../components/shared/AppPagination';
import {
  ProgressNotesListResponseType,
  getProgressNotesListApi,
  getProgressNotesListOnClientApi,
} from '../../services/progressNotes/ProgressNotesApi';
import { isCustomError, ApiError } from '../../services/ApiResponseHandler';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import EmptyScreen from '../../components/shared/EmptyScreen';
import {
  FACESHEET,
  PROGRESS_NOTE_CLIENT_SETUP,
  PROGRESS_NOTE_SETUP,
} from '../../routes/Routing';
import { useNavigate } from 'react-router-dom';
import CustomBackArrow from '../../components/imagepickers/backArrow';
import DisplayFilter, { FilterBy } from '../../components/shared/DisplayFilter';
import SearchNotFound from '../../components/formlib/SearchNotFound';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import {
  CLIENT_ID_KEY,
  REFERRAL_PROGRAM_KEY,
  STORAGE_USER_ID_KEY,
} from '../../services/Constant';
import { ReferralResponseType } from '../../services/configApi/Clients';
import { getAllEmployeeMinimalList } from '../../services/ifspClient/IfspApi';
import MultiSelectAutocompleteSearch, {
  MultiSelectOptioinType,
} from '../../components/formlib/MultiSelectAutocompleteSearch';
import { Employee } from '../../services/configApi/employees/employeeServices';
import CustomMenu from '../../components/formlib/CustomMenu';

const ITEMS_PER_PAGE = 10;

type ProgressNotesFilterType = {
  status: string;
  aptStartDate: DateString;
  aptEndDate: DateString;
  attendees: string[];
};

export interface ProgressNotesListData {
  progressNoteId?: string;
  lastName: string;
  enrollmentId: string;
  appointmentType: string;
  appointmentDate: string;
  attendees: string;
  startTime: string;
  duration: string;
  status: string;
  actions?: string;
}

function createHeadCell(
  id: keyof ProgressNotesListData,
  labelId: string,
  defaultLabelId: string,
  requiredSorting: boolean,
): HeadCell<ProgressNotesListData> {
  return {
    id,
    labelId,
    defaultLabelId,
    numeric: false,
    requiredSorting,
  };
}

function deepEqual(
  obj1: ProgressNotesFilterType,
  obj2: ProgressNotesFilterType,
): boolean {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

const HeadCells: HeadCell<ProgressNotesListData>[] = [
  createHeadCell('lastName', 'Dashboard.ClientName', 'Client Name', true),
  createHeadCell('enrollmentId', 'Facesheet.DPHID', 'Enrollment ID', false),
  createHeadCell(
    'appointmentType',
    'AddNewSchedulerEvent.appointmentType',
    'Appointment Type',
    false,
  ),
  createHeadCell(
    'appointmentDate',
    'ProgressNoteList.appointmentDatetitle',
    'Appointment Date',
    false,
  ),
  createHeadCell(
    'attendees',
    'ProgressNoteList.attendeestitle',
    'Attendees',
    false,
  ),
  createHeadCell('startTime', 'startTimeText', 'Start Time', false),
  createHeadCell('duration', 'ProgressNote.duration', 'Duration', false),
  createHeadCell('status', 'Dashboard.Status', 'Status', false),
  createHeadCell(
    'actions',
    'ProgressNotesList.listHeaderAction',
    'Action',
    false,
  ),
];

const validationSchema = yup.object().shape({
  status: yup.string().nullable(),
  aptStartDate: yup
    .string()
    .nullable()
    .test(
      'required-if-end-date',
      'ProgressNoteList.aptStartDateReq',
      function (value) {
        const { aptEndDate } = this.parent;
        if (aptEndDate && !value) {
          return false;
        }
        return true;
      },
    ),
  aptEndDate: yup
    .string()
    .nullable()
    .test(
      'required-if-start-date',
      'ProgressNoteList.aptEndDateReq',
      function (value) {
        const { aptStartDate } = this.parent;
        if (aptStartDate && !value) {
          return false;
        }
        return true;
      },
    ),
});
const validateForm = (value: ProgressNotesFilterType) => {
  const errors: Partial<ProgressNotesFilterType> = {};

  if (value.aptEndDate) {
    if (dayjs(value.aptEndDate).isBefore(dayjs(value.aptStartDate))) {
      errors.aptEndDate = 'endDateGreaterThanStartDate';
    }
    if (datePickerMinDateMaxDateValidate(value.aptEndDate)) {
      errors.aptEndDate = 'datePickerMinDateMaxDateValidate';
    }
  }

  if (datePickerMinDateMaxDateValidate(value.aptStartDate)) {
    errors.aptStartDate = 'datePickerMinDateMaxDateValidate';
  }
  return errors;
};

const ProgressNoteListView = () => {
  const navigate = useNavigate();
  const { loader, toggleLoader } = useContext(
    LoaderContext,
  ) as LoaderContextType;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [progressNotesListData, setProgressNotesListData] = useState<
    ProgressNotesListResponseType[]
  >([]);
  const [progressNotesListDataCount, setProgressNotesListDataCount] =
    useState(0);
  const formRef = useRef<FormikProps<ProgressNotesFilterType>>(null);
  const [page, setPage] = useState<number>(0);

  const [filterPlainData, setFilterPlainData] =
    useState<ProgressNotesFilterType>({
      status: '',
      aptStartDate: '',
      aptEndDate: '',
      attendees: [],
    });
  const [minApt, setMinApt] = useState<Date>();

  const [open, setOpen] = useState(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [routePath, setRoutePath] = useState('Clinician');
  const [searchquery, setSearchquery] = useState('');
  const [filterBy, setFilterBy] = useState<FilterBy[]>([]);
  const [showEmptyScreen, setShowEmptyScreen] = useState(false);
  const clientId = localStorage.getItem('ClientId');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof ProgressNotesListData>('lastName');

  const [employeeList, setEmployeeList] = useState<Employee[]>([]);

  const ClinicianHeadCells =
    checkPermissionForFeature('backend.progress_note', 'viewPermission') ===
    false
      ? HeadCells.slice(0, -1)
      : HeadCells;
  const ClientHeadCells =
    checkPermissionForFeature('backend.progress_note', 'viewPermission') ===
    false
      ? HeadCells.slice(2, -1)
      : HeadCells.slice(2);

  const MemorizedCustomMenu = useMemo(() => CustomMenu, []);

  const openit = Boolean(anchorEl);
  const currentUrl = document.location.pathname;

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof ProgressNotesListData,
  ) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createEmployeeObj = (
    id: string,
    name: string | null,
    email: string | null,
    firstName: string | null,
    lastName: string | null,
    disciplineType: string | null,
    phone: string | null,
  ) => {
    return {
      id: id,
      name: name,
      firstName: firstName,
      lastName: lastName,
      email: email,
      disciplineType: disciplineType,

      nameSuffix: '',
      clientId: '',
      kcUsername: '',
      middleName: '',
      preferredName: '',
      phone1: phone,
      phone2: '',
      workingHoursPerWeek: '',
      productivityGoal: null,
      allowProductivityGoalEdit: null,
      employeeRole: '',
      reportingTo: '',
      employeeType: '',
      startDate: '',
      endDate: '',
      status: '',
      primaryAddress1: '',
      primaryAddress2: '',
      primaryZipcode: '',
      primaryCity: '',
      primaryState: '',
      secondaryAddressRequired: false,
      secondaryAddress1: '',
      secondaryAddress2: '',
      secondaryZipcode: '',
      secondaryCity: '',
      secondaryState: '',
      emergencyFirstName: '',
      emergencyLastName: '',
      emergencyPhone: '',
      emergencyEmail: '',
      emergencyAddress1: '',
      emergencyAddress2: '',
      emergencyZipcode: '',
      emergencyCity: '',
      emergencyState: '',
      programs: [],
      modifiedOn: '',
      allowDelete: null,
      npi: null,
      employeeCredentials: null,
      signature: '',
      pin: '',
      phoneNumberExtension: '',
      phoneNumberExtension2: '',
      defaultProgram: '',
      superProgramId: '',
      lunchWaiverRequired: false,
      lunchWaiverStartDate: null,
      group: {
        id: '',
        groupName: '',
      },
    };
  };

  useEffect(() => {
    getAllEmployeeMinimalList()
      .then((data) => {
        let employeelist: Employee[] = [];
        employeelist = data.map((employee) => {
          return createEmployeeObj(
            employee.id,
            employee.name,
            employee.email,
            employee.firstName,
            employee.lastName,
            employee.disciplineType,
            employee.phone,
          );
        });

        setEmployeeList([...employeelist]);
      })
      .catch((error) => {
        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
          setToastrVariable('error');
        } else {
          setToastrId('failedToGetEmployee');
          setToastrDefaultMessage('Failed to get employee data');
          setToastrVariable('error');
        }
      });
  }, []);

  useEffect(() => {
    if (currentUrl === '/client/progress-note') {
      setRoutePath('Client');
      getProgressNotesListOnClient();
    } else {
      setRoutePath('Clinician');
      getProgressNotesList();
    }
  }, [searchquery, page, filterPlainData, orderBy, order]);

  const getProgressNotesList = async () => {
    toggleLoader(true);
    await getProgressNotesListApi(
      searchquery,
      filterPlainData.status,
      filterPlainData.aptStartDate
        ? filterPlainData.aptStartDate.toString()
        : '',
      filterPlainData.aptEndDate ? filterPlainData.aptEndDate.toString() : '',
      filterPlainData.attendees ?? [],
      page,
      ITEMS_PER_PAGE,
      orderBy,
      order,
    )
      .then((response) => {
        if (response) {
          setProgressNotesListData(response.progressNotes);
          setProgressNotesListDataCount(response.count);
          if (response.count === 0 && page === 0) {
            setShowEmptyScreen(true);
          } else {
            setShowEmptyScreen(false);
          }
          toggleLoader(false);
        } else {
          toggleLoader(false);
        }
      })
      .catch((error) => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('ConsentForms.concentLoadError');
          setToastrDefaultMessage('Failed to load Consent details');
        }
      });
  };

  const getProgressNotesListOnClient = async () => {
    setProgressNotesListData([]);

    let referralProgramId;
    const referralId = localStorage.getItem('referralId');
    const clientReferralDetails = JSON.parse(
      localStorage.getItem('clientReferralDetails') as unknown as string,
    ) as ReferralResponseType[];

    if (
      referralId &&
      clientReferralDetails &&
      clientReferralDetails.length > 0
    ) {
      referralProgramId = clientReferralDetails.find(
        (referral: ReferralResponseType) => referral.referralId === referralId,
      )?.programId;
    }

    if (clientId && referralProgramId && referralProgramId !== '') {
      toggleLoader(true);
      await getProgressNotesListOnClientApi(
        clientId,
        searchquery,
        filterPlainData.status,
        filterPlainData.aptStartDate
          ? filterPlainData.aptStartDate.toString()
          : '',
        filterPlainData.aptEndDate ? filterPlainData.aptEndDate.toString() : '',
        page,
        ITEMS_PER_PAGE,
        referralProgramId,
      )
        .then((response) => {
          if (response) {
            setProgressNotesListData(response.progressNotes);
            setProgressNotesListDataCount(response.count);
            if (response.count === 0 && page === 0) {
              setShowEmptyScreen(true);
            } else {
              setShowEmptyScreen(false);
            }
            toggleLoader(false);
          } else {
            toggleLoader(false);
          }
        })
        .catch((error) => {
          toggleLoader(false);
          setOpen(true);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('ConsentForms.concentLoadError');
            setToastrDefaultMessage('Failed to load Consent details');
          }
        });
    }
  };

  const handlefilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlefilterClose = () => {
    setAnchorEl(null);
  };
  const handleFilterSubmit = (values: ProgressNotesFilterType) => {
    const filterDataBy: FilterBy[] = [];
    setFilterPlainData(values);
    if (values.aptStartDate != null && values.aptStartDate !== '') {
      filterDataBy.push({
        key: 'ProgressNoteList.appointmentDatetitle',
        value: values.aptStartDate + ' - ' + values.aptEndDate,
      });
    }
    if (values.status != null && values.status !== '') {
      filterDataBy.push({
        key: 'Dashboard.Status',
        value: values.status,
      });
    }
    if (values.attendees.length > 0) {
      filterDataBy.push({
        key: 'ViewSchedulerEvent.clinicians',
        value: values.attendees.map((attendeeId) => {
          const item = employeeList.find((e) => e.id === attendeeId);
          return {
            id: item?.id?.toString() ?? '',
            label: `${item?.lastName ?? ''}, ${item?.firstName ?? ''}`,
          };
        }),
      });
    }
    setFilterBy(filterDataBy);
    if (page >= 0) {
      setPage(0);
    }

    if (filterDataBy.length <= 2) {
      if (filterDataBy.some((item) => item.value === 'false')) {
        clearForm();
        setFilterBy([]);
      }
    }
    setAnchorEl(null);
  };
  const handleClose = (reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const onViewClick = (selectedRow: ProgressNotesListResponseType) => {
    const userId = localStorage.getItem(STORAGE_USER_ID_KEY);
    const selectedClientId = localStorage.getItem(CLIENT_ID_KEY);
    const referralProgramId = localStorage.getItem(REFERRAL_PROGRAM_KEY);
    const isLoggedInUser = userId === selectedRow.attendeeId;
    if (routePath === 'Client') {
      navigate(PROGRESS_NOTE_CLIENT_SETUP, {
        state: {
          sortRequired: isLoggedInUser,
          eventId: selectedRow.commonAppointmentId,
          attendeeId: [{ id: selectedRow.attendeeId }],
          clientIds: [selectedClientId],
          screenFrom: 'ProgressNoteFacesheet',
          referralProgramId: referralProgramId,
        },
      });
    } else {
      navigate(PROGRESS_NOTE_SETUP, {
        state: {
          sortRequired: isLoggedInUser,
          eventId: selectedRow.commonAppointmentId,
          attendeeId: [{ id: selectedRow.attendeeId }],
          clientIds: [selectedRow.clientId],
          screenFrom: 'ProgressNoteEmployee',
          referralProgramId: referralProgramId,
        },
      });
    }
  };

  const isValidInput = (value: string) => {
    const allowedCharacters = /^[a-zA-Z0-9,'.\s!-]*$/;
    return allowedCharacters.test(value);
  };

  const handleSearchChange = useCallback(
    debounce((value: string) => {
      if (isValidInput(value)) {
        setPage(0);
        setSearchquery(value);
      } else {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('Dashboard.ClientList.searchError');
        setToastrDefaultMessage(
          'Only alphanumeric characters, commas, periods, spaces, exclamation marks, and hyphens are allowed to be searched.',
        );
      }
    }, 500),
    [],
  );

  const clearForm = () => {
    setPage(0);
    formRef.current?.resetForm();
    setFilterPlainData({
      aptStartDate: '',
      aptEndDate: '',
      status: '',
      attendees: [],
    });
    setFilterBy([]);
    setAnchorEl(null);
  };

  return (
    <>
      <SnackBarComponent
        open={open}
        handleClose={handleClose}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: '#E7F0F0',
          padding: '24px',
        }}
      >
        <Toolbar disableGutters>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Box className="flex__ align__items__center">
              {routePath === 'Client' && (
                <Box className="flex__ mr-md">
                  <CustomBackArrow onClick={() => navigate(FACESHEET)} />
                </Box>
              )}
              <Box sx={{ flexGrow: 1 }}>
                <TitleText
                  labelid={
                    routePath === 'Client'
                      ? 'ProgressNoteList.title'
                      : 'ProgressNoteList.myProgressNotestitle'
                  }
                  defaultlabel="My Progress Notes"
                />
              </Box>
              <Box sx={{ flexGrow: 0, mx: '24px' }}>
                <SearchBox
                  labelId={
                    routePath === 'Client'
                      ? 'ProgressNoteList.searchAttendee'
                      : 'Clientdashboard.searchText'
                  }
                  defaultlabel={
                    routePath === 'Client' ? 'Search Attendee' : 'Search Client'
                  }
                  onChange={handleSearchChange}
                />
              </Box>
              <Box
                sx={{
                  flexGrow: 0,
                }}
              >
                <Button
                  className="btn-primary btn-filter"
                  variant="outlined"
                  onClick={handlefilter}
                  endIcon={<KeyboardArrowDownIcon />}
                  startIcon={<Filtericon />}
                >
                  <FormattedMessage id="FiterText" defaultMessage="Filter" />
                </Button>
                <MemorizedCustomMenu
                  id="demo-customized-menu"
                  anchorEl={anchorEl}
                  open={openit}
                  onClose={handlefilterClose}
                >
                  <Formik
                    innerRef={formRef}
                    initialValues={filterPlainData}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                      handleFilterSubmit(values);
                    }}
                    validate={validateForm}
                  >
                    {({
                      setFieldValue,
                      handleSubmit,
                      errors,
                      touched,
                      values,
                    }) => (
                      <Grid container rowSpacing={2} columnSpacing={1}>
                        <Grid item xs={5.75}>
                          <DatePickerComponent
                            name="aptStartDate"
                            value={
                              values.aptStartDate !== null
                                ? dayjs(values.aptStartDate)
                                : null
                            }
                            labelid="ProgressNoteList.aptStartDatetitle"
                            defaultlabelid="Appointment Start Date"
                            handlechange={(date: Dayjs | null) => {
                              const formattedDate =
                                dayjs(date).format('MM/DD/YYYY');
                              if (date === null) {
                                setFieldValue('aptStartDate', null);
                              } else {
                                setFieldValue('aptStartDate', formattedDate);
                              }
                              if (
                                values &&
                                values.aptEndDate !== null &&
                                values.aptEndDate > formattedDate
                              ) {
                                setFieldValue('aptEndDate', null);
                              }
                              const newEndDate = dayjs(formattedDate).toDate();
                              setMinApt(newEndDate);
                              setFieldValue('aptEndDate', null);
                            }}
                          />
                          {errors.aptStartDate && touched.aptStartDate && (
                            <MediumTypography
                              labelid={errors.aptStartDate}
                              textColor="red"
                              marginTop="8px"
                            />
                          )}
                        </Grid>
                        <Grid item xs={0.5}>
                          <MediumTypography
                            labelid="Dashboard.to"
                            defaultlabel="to"
                            sxProps={{
                              textAlign: 'center',
                              margin: 'revert',
                            }}
                          />
                        </Grid>
                        <Grid item xs={5.75}>
                          <DatePickerComponent
                            name="aptEndDate"
                            value={
                              values.aptEndDate !== null
                                ? dayjs(values.aptEndDate)
                                : null
                            }
                            minDate={dayjs(minApt)}
                            labelid="ProgressNoteList.aptEndDatetitle"
                            defaultlabelid="Appointment End Date"
                            handlechange={(date: Dayjs | null) => {
                              const formattedDate =
                                dayjs(date).format('MM/DD/YYYY');
                              if (date === null) {
                                setFieldValue('aptEndDate', null);
                              } else {
                                setFieldValue('aptEndDate', formattedDate);
                              }
                            }}
                          />
                          {errors.aptEndDate && touched.aptEndDate && (
                            <MediumTypography
                              labelid={errors.aptEndDate}
                              textColor="red"
                              marginTop="8px"
                            />
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <DropdownComponent
                            labelid="Dashboard.Status"
                            defaultlabelid="Status"
                            name="status"
                            value={values.status}
                            handleChange={(value: string) => {
                              setFieldValue('status', value);
                            }}
                            names={progessNotesStatusFilter}
                          />
                        </Grid>
                        {routePath === 'Clinician' && (
                          <Grid item xs={12} className="multisearchHgt">
                            <MultiSelectAutocompleteSearch
                              labelId="ViewSchedulerEvent.clinicians"
                              defaultId="Attendee(s)"
                              onlyName={false}
                              onSelectItem={(
                                value: MultiSelectOptioinType[],
                              ) => {
                                if (value) {
                                  const ids = value.map((item) => item.id);
                                  if (
                                    employeeList.length > 0 ||
                                    ids.length > 0
                                  ) {
                                    setFieldValue('attendees', ids);
                                  } else {
                                    setFieldValue('attendees', []);
                                  }
                                }
                              }}
                              value={
                                values.attendees
                                  .map((id) =>
                                    employeeList.find((item) => item.id === id),
                                  )
                                  .filter(Boolean) as MultiSelectOptioinType[]
                              }
                              data={employeeList}
                              hideOption={null}
                              optionHintId={
                                'AddNewSchedulerEvent.noCliniciansFoundInOptions'
                              }
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} textAlign={right} mt={1.5}>
                          <Box
                            sx={{ justifyContent: 'right', display: 'flex' }}
                          >
                            <Box className="mr-md">
                              <ButtonComponent
                                className="btn-primary btn-cancel btn_width_90px"
                                variantType="contained"
                                labelId="Dashboard.filtercancel"
                                defaultLabelId="Cancel"
                                onClick={() => {
                                  setAnchorEl(null);
                                }}
                                disabled={loader}
                              />
                            </Box>
                            <Box>
                              <ButtonComponent
                                className="btn-primary btn-submit btn_width_90px"
                                variantType="contained"
                                type="submit"
                                labelId="Dashboard.filtersubmit"
                                defaultLabelId="Apply"
                                onClick={() => {
                                  if (deepEqual(filterPlainData, values)) {
                                    return;
                                  }
                                  handleSubmit();
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                  </Formik>
                </MemorizedCustomMenu>
              </Box>
            </Box>
            {(filterPlainData.aptStartDate !== '' ||
              filterPlainData.aptEndDate !== '' ||
              filterPlainData.status !== '' ||
              filterPlainData.attendees.length > 0) && (
              <DisplayFilter
                filteredData={filterBy}
                handleCloseClick={(index: number) => {
                  if (filterBy.length < 2) {
                    clearForm();
                  } else {
                    setFilterPlainData({
                      aptStartDate:
                        filterBy[index].key ===
                        'ProgressNoteList.appointmentDatetitle'
                          ? ''
                          : filterPlainData.aptStartDate,
                      aptEndDate:
                        filterBy[index].key ===
                        'ProgressNoteList.appointmentDatetitle'
                          ? ''
                          : filterPlainData.aptEndDate,
                      status:
                        filterBy[index].key === 'Dashboard.Status'
                          ? ''
                          : filterPlainData.status,
                      attendees: filterPlainData.attendees,
                    });
                    const newFilterBy = filterBy.filter((_, i) => i !== index);
                    setFilterBy(newFilterBy);
                  }
                }}
                handleCloseClickArrayType={(main: number, sub: number) => {
                  if (filterBy[main].key === 'ViewSchedulerEvent.clinicians') {
                    setFilterPlainData({
                      ...filterPlainData,
                      attendees: filterPlainData.attendees.filter(
                        (item, index) => index !== sub,
                      ),
                    });

                    setFilterBy((prevFilterBy) => {
                      const newFilterBy = [...prevFilterBy];

                      if (Array.isArray(newFilterBy[main].value)) {
                        newFilterBy[main].value = (
                          newFilterBy[main].value as OptionType[]
                        ).filter((_, indexf) => indexf !== sub);
                      }

                      if (newFilterBy[main].value.length === 0) {
                        newFilterBy.splice(main, 1);
                      }
                      return newFilterBy;
                    });
                  }
                }}
                handleClearClick={() => {
                  clearForm();
                }}
              />
            )}
          </Box>
        </Toolbar>

        {progressNotesListData.length > 0 && (
          <Card>
            <TableContainer component={Paper}>
              <Table aria-label="simple table">
                <TableHeader
                  className="listDataTableHead"
                  headerNames={
                    routePath === 'Clinician'
                      ? ClinicianHeadCells
                      : ClientHeadCells
                  }
                  order={order}
                  orderBy={orderBy}
                  checkBoxRequired={false}
                  onRequestSort={handleRequestSort}
                />
                <TableBody className="tableRowcss">
                  {progressNotesListData.length !== 0 &&
                    progressNotesListData.map((progressNotesData) => (
                      <ProgressNotesTableRow
                        key={progressNotesData.progressNotesId}
                        data={progressNotesData}
                        routePath={routePath}
                        onView={onViewClick}
                      />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
        {progressNotesListData.length > 0 &&
          progressNotesListDataCount > ITEMS_PER_PAGE && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <AppPagination
                pageNumber={page}
                paginationCount={progressNotesListDataCount}
                handleChangePage={(_event, newPage) => {
                  if (newPage - 1 !== page) {
                    setPage(newPage - 1);
                  }
                }}
              />
            </Box>
          )}
      </Box>

      {filterBy.length === 0 &&
        searchquery === '' &&
        showEmptyScreen &&
        progressNotesListData.length === 0 && (
          <Box component="main" className="mb-md">
            <Box component="section">
              <EmptyScreen
                titleLabelId="noProgressFoundText"
                defaultTitleText="No Progress Notes found yet"
                showButton={false}
              />
            </Box>
          </Box>
        )}

      {filterBy.length > 0 &&
        progressNotesListData.length === 0 &&
        showEmptyScreen && <SearchNotFound />}
      {searchquery != '' && progressNotesListData.length === 0 && (
        <SearchNotFound />
      )}
    </>
  );
};

export default ProgressNoteListView;
